import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { Avatar } from '../../Avatar'
import { durationLabel, MAX_AVATARS, profileUrl, tileUserIds, timeAgo, type DiscoverPost, type DiscoverUser } from './discoverData'

interface Props {
  post: DiscoverPost
  users: Record<number, DiscoverUser>
  canWrite: boolean
  onOpen: () => void
  onCollaborators: (ids: number[]) => void
  /** Bookmark the post (the bookmark button's first click). */
  onBookmark: () => Promise<void>
  onRemoveBookmark: () => Promise<void>
  onAddToCollection: () => void
}

/** OnlyFans hides the bookmark menu by itself 10 s after it opened. */
const MENU_TIMEOUT = 10_000

const openProfile = (url: string) => window.open(url, '_blank', 'noopener,noreferrer')

/** One OFTV video (DiscoverItem): picture, creators and age on top, length · likes and the bookmark below. */
export function DiscoverTile({ post, users, canWrite, onOpen, onCollaborators, onBookmark, onRemoveBookmark, onAddToCollection }: Props) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const { ref, src } = useMediaSrc(post.media?.preview ?? '')
  const ids = tileUserIds(post, users)
  const first = users[ids[0]!]
  const second = ids.length === 2 ? users[ids[1]!] : undefined
  const many = ids.length >= 3
  const isVideo = post.media?.isVideo === true
  const isPhoto = Boolean(post.media) && !isVideo

  const [menu, setMenu] = useState(false)
  const [busy, setBusy] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const closeMenu = () => {
    window.clearTimeout(timer.current)
    setMenu(false)
  }

  const run = async (work: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    try {
      await work()
    } finally {
      setBusy(false)
    }
  }

  const bookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (menu) return closeMenu()
    setMenu(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setMenu(false), MENU_TIMEOUT)
    if (!post.isAddedToBookmarks) void run(onBookmark)
  }

  const avatarsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (ids.length === 1) openProfile(profileUrl(first, ids[0]!))
    else onCollaborators(ids)
  }

  const nameLink = (id: number, user: DiscoverUser | undefined) => (
    <a className="ofdc-link" href={profileUrl(user, id)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
      {user?.name ?? ''}
    </a>
  )

  return (
    <div className="ofdc-card" role="button" tabIndex={0} aria-label={t('play')} onClick={onOpen} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget && (e.preventDefault(), onOpen())}>
      <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofdc-img">
        {src && <img src={src} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />}
      </span>
      {isVideo && (
        <span className="ofdc-play" aria-hidden="true">
          <FeatherIcon name="play" size={16} />
        </span>
      )}
      <div className="ofdc-tile-head">
        <div className="ofdc-users">
          <div className="ofdc-users-main">
            <span className="ofdc-avatars" onClick={avatarsClick}>
              {ids.slice(0, MAX_AVATARS).map((id) => (
                <span key={id} className="ofdc-avatar">
                  <Avatar user={users[id]} size={24} />
                </span>
              ))}
            </span>
            <span className={`ofdc-name ${ids.length > 1 ? 'is-few' : ''}`}>{nameLink(ids[0]!, first)}</span>
          </div>
          {second && (
            <span className="ofdc-more">
              {t('and')} {nameLink(ids[1]!, second)}
            </span>
          )}
          {many && (
            <span
              className="ofdc-more"
              onClick={(e) => {
                e.stopPropagation()
                onCollaborators(ids)
              }}
            >
              {t('and')}{' '}
              <span className="ofdc-link">
                {ids.length - 1} {t('others')}
              </span>
            </span>
          )}
        </div>
        <span className="ofdc-timer">{timeAgo(post.postedAt)}</span>
      </div>
      <div className="ofdc-foot">
        <div className="ofdc-counters">
          {isVideo && (
            <span className="ofdc-count" aria-label={t('duration')}>
              <FeatherIcon name="video" size={14} />
              <span>{durationLabel(post.media?.duration ?? 0)}</span>
            </span>
          )}
          {isPhoto && (
            <span className="ofdc-count" aria-label={t('media')}>
              <FeatherIcon name="image" size={14} />
              <span>{post.mediaCount}</span>
            </span>
          )}
          {post.favoritesCount > 0 && (
            <span className="ofdc-count" aria-label={t('likes')}>
              <FeatherIcon name="heart" size={14} />
              <span>{post.favoritesCount}</span>
            </span>
          )}
        </div>
        <span className="ofdc-bm-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`ofdc-bm ${post.isAddedToBookmarks ? 'is-on' : ''}`}
            aria-label={post.isAddedToBookmarks ? t('removeFromBookmarks') : t('addToBookmarks')}
            aria-haspopup="menu"
            aria-expanded={menu}
            title={canWrite ? undefined : t('readOnly')}
            disabled={!canWrite}
            onClick={bookmarkClick}
          >
            <FeatherIcon name="bookmark" size={14} />
          </button>
          {menu && (
            <>
              <div className="lib-backdrop" onClick={closeMenu} />
              <div className="lib-pop lib-menu ofdc-bm-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="lib-menu-item lib-menu-item--danger"
                  disabled={busy}
                  onClick={() => {
                    closeMenu()
                    if (post.isAddedToBookmarks) void run(onRemoveBookmark)
                  }}
                >
                  <span className="lib-menu-label">{t('removeBookmark')}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="lib-menu-item"
                  onClick={() => {
                    closeMenu()
                    onAddToCollection()
                  }}
                >
                  <span className="lib-menu-label">{t('addToCollection')}</span>
                </button>
              </div>
            </>
          )}
        </span>
      </div>
    </div>
  )
}
